import { Injectable } from '@angular/core';
import { IRegistryCurrentState } from '../../registry/interfaces/iregistry-current-state';
import { AnyKeyAnyType } from '../../shared-third-party/common.type';

interface IFilter {
  condition: string;
  property: string;
  value: string | boolean | number | string[];
}

interface ISort {
  property: string;
  direction: string;
}

export interface IGridParams {
  filter: Record<string, IFilter> | string;
  sort?: ISort[];
  offset?: number;
  limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class GridService {
  static CONDITION_EQ = 'eq';
  static CONDITION_GT = 'gt';
  static CONDITION_GTE = 'gte';
  static CONDITION_LT = 'lt';
  static CONDITION_LTE = 'lte';
  static CONDITION_CONTAINS = 'contains';
  static CONDITION_IN = 'in';
  static CONDITION_NEQ = 'neq';
  static CONDITION_IS_NULL = 'isNull';
  static CONDITION_IS_NOT_NULL = 'isNotNull';
  static CONDITION_LIKE = 'like';
  static CONDITION_MATCH = 'match';
  static CONDITION_NOT_MATCH = 'notMatch';

  /**
   * Создает фильтрацию для бека по grid для get запроса
   */
  createGetFilter(
    data: Record<string, string | boolean | number | string[]>,
    conditions: Record<string, string>,
    page: string | null = null,
    limit: string | null = null,
    sort: Record<string, string> = {},
    needCalculateOffset = true,
  ): string {
    return GridService.buildQueryString(this.createFilter(data, conditions, page, limit, sort, needCalculateOffset));
  }

  /**
   * Создает фильтрацию для бека по grid для post запроса
   */
  createPostFilter(
    data: Record<string, string | boolean | number | string[]>,
    conditions: Record<string, string>,
    page: string | null = null,
    limit: string | null = null,
    sort: Record<string, string> = {},
    needCalculateOffset = true,
  ): IGridParams {
    return this.createFilter(data, conditions, page, limit, sort, needCalculateOffset);
  }

  /**
   * Создает фильтрацию для бека по grid
   */
  private createFilter(
    data: Record<string, string | boolean | number | string[]>,
    conditions: Record<string, string>,
    page: string | null = null,
    limit: string | null = null,
    sort: Record<string, string> = {},
    needCalculateOffset = true,
  ): IGridParams {
    const filter = new Map<string, IFilter>();
    Object.entries(data).forEach(([key, value]) => {
      if (
        key === 'page' ||
        key === 'limit' ||
        key === 'sortDirection' ||
        key === 'sort' ||
        value === null ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      if (value instanceof Array && value.length === 0) {
        return;
      }

      filter.set(key, {
        condition: conditions[key],
        property: key,
        value: value,
      });
    });

    const filterObject: Record<string, IFilter> = Array.from(filter.entries(), ([, value]) => value).reduce(
      (obj: Record<string, IFilter>, value: IFilter) => {
        obj[value.property] = value;

        return obj;
      },
      {},
    );

    const generatedSort: ISort[] = [];
    Object.entries(sort).forEach(([key]) => {
      if (key === 'page' || key === 'limit') {
        return;
      }

      generatedSort.push({
        property: key,
        direction: sort[key],
      });
    });

    const params: IGridParams = {
      filter: filterObject,
    };

    if (generatedSort.length > 0) {
      params.sort = generatedSort;
    }

    if (limit && page) {
      params.limit = parseInt(limit, 10);
      if (needCalculateOffset) {
        params.offset = (parseInt(page, 10) - 1) * params.limit;
      } else {
        params.offset = parseInt(page, 10);
      }
    }

    return params;
  }

  /**
   * Создает параметры сортировки
   */
  createSortParams(queryParams: IRegistryCurrentState, propertyKeys: string[]): Record<string, string> {
    const data: Record<string, string> = {};

    for (const queryParam in queryParams) {
      if (propertyKeys.indexOf(queryParam) === -1) {
        continue;
      }

      data[queryParam as string] = queryParams[queryParam];
    }

    return data;
  }

  /**
   * Формирует строку из параметров для гет запроса
   */
  static buildQueryString(params: AnyKeyAnyType, prefix = ''): string {
    const parts: string[] = [];

    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) {
        continue;
      }

      const value = params[key];

      if (value === null || value === undefined) {
        continue;
      }

      const paramKey = prefix ? prefix + '[' + key + ']' : key;

      if (typeof value === 'object') {
        parts.push(GridService.buildQueryString(value, paramKey));
      } else {
        parts.push(encodeURIComponent(paramKey) + '=' + encodeURIComponent(value));
      }
    }

    return parts.join('&');
  }
}
